import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { Link, useLocation, useParams, withRouter } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import Loader from "../../Components/Common/Loader";
import {
  useAdminConversationMessages,
  useAdminConversations,
  useAdminDirectory,
  useCreateAdminConversation,
  useSendAdminMessage,
} from "../../hooks/useAdminMessaging";
import {
  disconnectAdminChatSocket,
  emitAdminTypingStart,
  emitAdminTypingStop,
  getAdminChatSocket,
  joinAdminConversation,
  leaveAdminConversation,
  subscribeAdminChatEvents,
  unsubscribeAdminChatEvents,
} from "../../services/adminChatSocket";
import { getStoredAuthUser } from "../../utils/backendApi";
import { normalizeMessage } from "../../services/adminMessagingService";

const AdminMessaging = (props) => {
  const authUser = getStoredAuthUser();
  const { id } = useParams();
  const location = useLocation();
  const qc = useQueryClient();
  const conversationsQ = useAdminConversations();
  const directoryQ = useAdminDirectory();
  const createConversationMutation = useCreateAdminConversation();
  const sendMessageMutation = useSendAdminMessage();
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState([]);
  const [typingUserIds, setTypingUserIds] = useState([]);
  const socketRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const conversations = conversationsQ.data || [];
  const directory = useMemo(
    () =>
      (directoryQ.data || []).filter(
        (user) => Number(user?.id) !== Number(authUser?.id)
      ),
    [directoryQ.data, authUser?.id]
  );

  const selectedConversation = useMemo(
    () =>
      conversations.find(
        (conversation) =>
          Number(conversation.id) === Number(selectedConversationId)
      ) || null,
    [conversations, selectedConversationId]
  );

  const existingConversationForUser = useMemo(
    () =>
      conversations.find(
        (conversation) =>
          Number(conversation?.otherUser?.id) === Number(selectedUserId)
      ) || null,
    [conversations, selectedUserId]
  );

  const messagesQ = useAdminConversationMessages(selectedConversationId, {
    enabled: Boolean(selectedConversationId),
  });

  useEffect(() => {
    if (!conversations.length) return;

    const routeId = Number(id);
    const matchingConversation = conversations.find(
      (conversation) => Number(conversation.id) === routeId
    );

    if (matchingConversation) {
      setSelectedConversationId(matchingConversation.id);
      return;
    }

    if (!selectedConversationId) {
      setSelectedConversationId(conversations[0].id);
    }
  }, [conversations, id, selectedConversationId]);

  useEffect(() => {
    if (!selectedConversationId) {
      setMessages([]);
      return;
    }
    setMessages(messagesQ.data || []);
  }, [messagesQ.data, selectedConversationId]);

  useEffect(() => {
    if (!messagesContainerRef.current) return;
    messagesContainerRef.current.scrollTop =
      messagesContainerRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!selectedConversationId) return undefined;

    let socket;

    try {
      socket = getAdminChatSocket();
      socketRef.current = socket;
      subscribeAdminChatEvents(socket, {
        onHistory: (items) => {
          setMessages((items || []).map(normalizeMessage));
        },
        onNewMessage: (item) => {
          const normalized = normalizeMessage(item);
          if (Number(normalized.conversation) !== Number(selectedConversationId)) {
            qc.invalidateQueries({ queryKey: ["admin", "conversations"] });
            return;
          }
          setMessages((current) => {
            if (current.some((message) => Number(message.id) === Number(normalized.id))) {
              return current;
            }
            return [...current, normalized];
          });
          qc.invalidateQueries({ queryKey: ["admin", "conversations"] });
        },
        onTypingStart: ({ userId }) => {
          if (Number(userId) === Number(authUser?.id)) return;
          setTypingUserIds((current) =>
            current.includes(userId) ? current : [...current, userId]
          );
        },
        onTypingStop: ({ userId }) => {
          setTypingUserIds((current) =>
            current.filter((item) => Number(item) !== Number(userId))
          );
        },
        onError: ({ message }) => {
          toast.error(message || props.t("Erreur de messagerie"), {
            autoClose: 3000,
          });
        },
      });
      joinAdminConversation(socket, selectedConversationId);
    } catch (error) {
      toast.error(error?.message || props.t("Connexion temps réel indisponible"), {
        autoClose: 3000,
      });
    }

    return () => {
      if (socket) {
        leaveAdminConversation(socket, selectedConversationId);
        unsubscribeAdminChatEvents(socket);
        disconnectAdminChatSocket();
        socketRef.current = null;
      }
      setTypingUserIds([]);
    };
  }, [selectedConversationId, authUser?.id, props, qc]);

  const openConversation = async () => {
    if (!selectedUserId) return;
    if (existingConversationForUser) {
      setSelectedConversationId(existingConversationForUser.id);
      return;
    }
    try {
      const conversation = await createConversationMutation.mutateAsync(selectedUserId);
      setSelectedConversationId(conversation.id);
      qc.invalidateQueries({ queryKey: ["admin", "conversations"] });
    } catch (error) {
      toast.error(error?.message || props.t("Conversation non créée"), {
        autoClose: 3000,
      });
    }
  };

  const handleTyping = (value) => {
    setDraft(value);

    if (!selectedConversationId || !socketRef.current) return;

    emitAdminTypingStart(socketRef.current, selectedConversationId);

    if (typingTimeoutRef.current) {
      window.clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = window.setTimeout(() => {
      if (socketRef.current) {
        emitAdminTypingStop(socketRef.current, selectedConversationId);
      }
    }, 1200);
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    const content = draft.trim();
    if (!content) return;

    let conversationId = selectedConversationId;

    if (!conversationId && selectedUserId) {
      const conversation = await createConversationMutation.mutateAsync(selectedUserId);
      conversationId = conversation.id;
      setSelectedConversationId(conversation.id);
    }

    if (!conversationId) {
      toast.error(props.t("Sélectionnez d'abord un utilisateur"), {
        autoClose: 3000,
      });
      return;
    }

    setDraft("");

    try {
      if (socketRef.current?.connected) {
        emitAdminTypingStop(socketRef.current, conversationId);
        socketRef.current.emit("message:send", {
          conversationId: Number(conversationId),
          content,
        });
      } else {
        const created = await sendMessageMutation.mutateAsync({
          conversationId,
          senderId: authUser?.id,
          content,
        });
        setMessages((current) => [...current, created]);
      }
      qc.invalidateQueries({ queryKey: ["admin", "conversations"] });
    } catch (error) {
      toast.error(error?.message || props.t("Message non envoyé"), {
        autoClose: 3000,
      });
      setDraft(content);
    }
  };

  const conversationItems = useMemo(() => {
    const values = [...conversations];
    values.sort((left, right) => {
      const rightTime = right?.lastMessage?.timestamp || right?.created_at || "";
      const leftTime = left?.lastMessage?.timestamp || left?.created_at || "";
      return new Date(rightTime).getTime() - new Date(leftTime).getTime();
    });
    return values;
  }, [conversations]);

  document.title = props.t("Messagerie assistance - Le Conseiller du BTP");

  if (conversationsQ.isLoading || directoryQ.isLoading) {
    return <Loader />;
  }

  if (conversationsQ.isError || directoryQ.isError) {
    return (
      <Loader
        error={
          conversationsQ.error?.message ||
          directoryQ.error?.message ||
          props.t("Erreur lors du chargement")
        }
      />
    );
  }

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} />
      <Container fluid>
        <BreadCrumb
          title={props.t("Assistance")}
          pageTitle={props.t("Messagerie instantanée")}
        />
        <Row>
          <Col xl={4}>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div>
                    <h5 className="mb-0">{props.t("Conversations")}</h5>
                  </div>
                  <Badge color="soft-primary" className="text-primary">
                    {conversationItems.length}
                  </Badge>
                </div>
              </CardHeader>
              <CardBody>
                <div className="mb-3">
                  <Input
                    type="select"
                    value={selectedUserId}
                    onChange={(event) => setSelectedUserId(event.target.value)}
                  >
                    <option value="">{props.t("Sélectionner un utilisateur")}</option>
                    {directory.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.displayName} - {user.email || user.username}
                      </option>
                    ))}
                  </Input>
                </div>
                <div className="mb-3 d-flex gap-2">
                  <Button color="primary" onClick={openConversation} disabled={!selectedUserId}>
                    {existingConversationForUser
                      ? props.t("Ouvrir la conversation")
                      : props.t("Démarrer une conversation")}
                  </Button>
                </div>
                <div style={{ maxHeight: 520, overflowY: "auto" }}>
                  {conversationItems.length === 0 ? (
                    <div className="text-muted">{props.t("Aucune conversation")}</div>
                  ) : (
                    conversationItems.map((conversation) => {
                      const active =
                        Number(conversation.id) === Number(selectedConversationId);
                      return (
                        <button
                          key={conversation.id}
                          type="button"
                          className={`w-100 text-start border-0 bg-transparent px-0 ${
                            active ? "fw-bold" : ""
                          }`}
                          onClick={() => {
                            setSelectedConversationId(conversation.id);
                            setSelectedUserId(String(conversation.otherUser?.id || ""));
                          }}
                        >
                          <div className="border rounded p-3 mb-2">
                            <div className="d-flex justify-content-between gap-2">
                              <div>
                                <div>{conversation.otherUser?.displayName}</div>
                                <div className="text-muted small">
                                  {conversation.otherUser?.email ||
                                    conversation.otherUser?.username}
                                </div>
                              </div>
                              <div className="text-muted small">
                                {conversation.lastMessage?.timestamp
                                  ? moment(conversation.lastMessage.timestamp).format(
                                      "DD/MM HH:mm"
                                    )
                                  : ""}
                              </div>
                            </div>
                            <div className="text-muted small mt-2">
                              {conversation.lastMessage?.content ||
                                props.t("Conversation créée")}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl={8}>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h5 className="mb-0">
                      {selectedConversation?.otherUser?.displayName ||
                        props.t("Nouvelle conversation")}
                    </h5>
                    <div className="text-muted small">
                      {selectedConversation?.otherUser?.email ||
                        props.t("Choisissez un utilisateur pour commencer")}
                    </div>
                  </div>
                  {selectedConversation?.id ? (
                    <Link to={`${location.pathname.replace(/\/$/, "")}`}>
                      <Badge color="soft-info" className="text-info">
                        #{selectedConversation.id}
                      </Badge>
                    </Link>
                  ) : null}
                </div>
              </CardHeader>
              <CardBody>
                <div
                  ref={messagesContainerRef}
                  className="border rounded p-3 mb-3"
                  style={{ minHeight: 420, maxHeight: 420, overflowY: "auto" }}
                >
                  {messages.length === 0 ? (
                    <div className="text-muted text-center pt-5">
                      {props.t("Aucun message pour le moment")}
                    </div>
                  ) : (
                    messages.map((message) => {
                      const isMine =
                        Number(message.sender) === Number(authUser?.id);
                      return (
                        <div
                          key={message.id}
                          className={`d-flex mb-3 ${
                            isMine ? "justify-content-end" : "justify-content-start"
                          }`}
                        >
                          <div
                            className={`rounded px-3 py-2 ${
                              isMine ? "bg-primary text-white" : "bg-light"
                            }`}
                            style={{ maxWidth: "70%" }}
                          >
                            <div>{message.content}</div>
                            <div
                              className={`small mt-1 ${
                                isMine ? "text-white-50" : "text-muted"
                              }`}
                            >
                              {moment(message.timestamp).format("DD/MM/YYYY HH:mm")}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                {typingUserIds.length > 0 ? (
                  <div className="text-muted small mb-2">
                    {props.t("L’utilisateur est en train d’écrire...")}
                  </div>
                ) : null}
                <form onSubmit={handleSendMessage}>
                  <div className="d-flex gap-2">
                    <Input
                      type="textarea"
                      rows="3"
                      value={draft}
                      onChange={(event) => handleTyping(event.target.value)}
                      placeholder={props.t("Écrire un message")}
                    />
                    <Button
                      type="submit"
                      color="primary"
                      disabled={
                        sendMessageMutation.isPending ||
                        createConversationMutation.isPending
                      }
                    >
                      {props.t("Envoyer")}
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

AdminMessaging.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(AdminMessaging));
